import { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';

import { getInspectionsByMarket, InspectionByMarket } from '@/services/dashboardService';
import { LoadingSpinner } from '@/components/Dashboard/LoadingSpinner';
import { getCurrentMonth } from '@/utils/date';

import MonthSelector from './MonthSelector';
import './dashboard.css';
import { DataStatus } from './dataStatus';
import chartColors from './chartColors';
import { SMALL_SCREEN_WIDTH_LIMIT } from '@/common/constants';

type Market = '3rd' | 'tc';
const marketLabels: Market[] = ['3rd', 'tc'];
const emptyData: Record<Market, { inProgress: number; cancelled: number; completed: number; }> = {
    '3rd': { inProgress: 0, cancelled: 0, completed: 0 },
    'tc': { inProgress: 0, cancelled: 0, completed: 0 },
};

const { blue, red, green } = chartColors


const ChartInspectionsStatusByMarket = () => {
    const [grouping, setGrouping] = useState<'all' | 'inProgress' | 'completed'>('all');
    const { year: currentYear, month: currentMonth } = getCurrentMonth();

    const [data, setData] = useState(emptyData);
    const [dataStatus, setDataStatus] = useState<DataStatus>(DataStatus.LOADING);
    const [month, setMonth] = useState<number>(currentMonth);
    const [year, setYear] = useState<number>(currentYear);

    const transformData = (originalData: InspectionByMarket[]) => {
        const result = { ...emptyData };
        for (const market of marketLabels) {
            const item = originalData.find((value) => value.market.toLowerCase() === market);
            if (item) {
                result[market] = {
                    inProgress:
                        (item.inProgressNominationInspectionProcess ?? 0),
                    cancelled: item.cancelledNominationInspectionProcess ?? 0,
                    completed: (item.finishedInspectionProcess ?? 0),
                };
            }
        }
        return result;
    };

    const getData = async (month: number, year: number) => {
        setDataStatus(DataStatus.LOADING);
        const response = await getInspectionsByMarket(month, year);
        if (!response.data) {
            setDataStatus(DataStatus.ERROR);
            return;
        }
        if (response.data[0] == null) {
            setDataStatus(DataStatus.EMPTY);
            return;
        }
        const originalData = response.data as InspectionByMarket[];
        setData(transformData(originalData));
        setDataStatus(DataStatus.OK);
    };

    useEffect(() => {
        getData(currentMonth, currentYear);
    }, []);

    useEffect(() => {
        getData(month, year);
    }, [month, year]);


    // Prepare chart data for each market
    const getChartConfig = (market: Market) => {
        let chartSeries: number[] = [];
        let chartLabels: string[] = [];
        let chartColors: string[] = [];
        if (grouping === 'inProgress') {
            chartSeries = [data[market].inProgress ?? 0];
            chartLabels = ['In Progress'];
            chartColors = [blue];
        } else if (grouping === 'completed') {
            chartSeries = [data[market].completed ?? 0];
            chartLabels = ['Done'];
            chartColors = [green];
        } else {
            chartSeries = [data[market].inProgress ?? 0, data[market].cancelled ?? 0, data[market].completed ?? 0,];
            chartLabels = ['In Progress', 'Cancelled', 'Done'];
            chartColors = [blue, red, green];
        }
        return {
            series: chartSeries,
            options: {
                chart: { type: 'pie' },
                dataLabels: {
                    formatter: (_val: number, opts: { w: { config: { series: number[] } }; seriesIndex: number }) => (opts.w.config.series[opts.seriesIndex] ?? 0)
                },
                labels: chartLabels,
                legend: {
                    show: true,
                    position: "bottom",
                    formatter: function (seriesName: string, opts: { seriesIndex: number; w: { globals: { series: number[] } } }) {
                        const seriesIndex = opts.seriesIndex;
                        const value = opts.w.globals.series[seriesIndex];
                        return `${seriesName} (${value ?? 0})`;
                    }
                },
                colors: chartColors,
                responsive: [{
                    breakpoint: SMALL_SCREEN_WIDTH_LIMIT,
                    options: {
                        legend: {
                            position: "bottom",
                            formatter: (legendName: string, opts: { seriesIndex: number; w: { globals: { series: number[] } } }) => (`${legendName} (${opts.w.globals.series[opts.seriesIndex] ?? 0})`),
                        }
                    }
                }]
            }
        };
    };

    return (
        <div className='card' style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', width: '100%' }}>
            <div style={{ flex: 1}}>
                <h2 className='card-title'>Inspections by Inspecting Company - TC</h2>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <MonthSelector month={month} year={year} onChange={(selectedMonth, selectedYear) => { setMonth(selectedMonth); setYear(selectedYear) }} />
                    <div>
                        {/* Dropdown for grouping */}
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', alignItems: "center" }}>
                            <label htmlFor="grouping-select" style={{ marginLeft: '8px' }}>Group by:</label>
                            <select
                                id="grouping-select"
                                value={grouping}
                                onChange={e => setGrouping(e.target.value as 'all' | 'inProgress' | 'completed')}
                                style={{ padding: '4px 8px' }}
                            >
                                <option value="all">All Statuses</option>
                                <option value="inProgress">In Progress Only</option>
                                <option value="completed">Completed Only</option>
                            </select>
                        </div>
                    </div>
                </div>

                {(dataStatus === DataStatus.LOADING) && <LoadingSpinner />}
                {(dataStatus === DataStatus.EMPTY || dataStatus === DataStatus.ERROR) && <div>No data to display</div>}
                {(dataStatus === DataStatus.OK || dataStatus === DataStatus.LOADING) && (
                    (data['tc'].inProgress || data['tc'].completed || data['tc'].cancelled) ? (
                        <div style={{ borderRight: '1px solid #ddd' }}>
                            <ReactApexChart
                                type="pie"
                                // @ts-expect-error: ReactApexChart type definitions may not match the options structure
                                options={getChartConfig('tc').options}
                                series={getChartConfig('tc').series}
                                height={420}
                            />
                        </div>
                    ) : (
                        <div>No data to display</div>
                    )
                )}
            </div>
            <div style={{ flex: 1 }}>
                <h2 className='card-title'>Inspections by Inspecting Company - 3rd</h2>
                <div style={{ opacity: 0 }}>
                    <MonthSelector month={month} year={year} onChange={(selectedMonth, selectedYear) => { setMonth(selectedMonth); setYear(selectedYear) }} />
                </div>
                {(dataStatus === DataStatus.LOADING) && <LoadingSpinner />}
                {(dataStatus === DataStatus.EMPTY || dataStatus === DataStatus.ERROR) && <div>No data to display</div>}
                {(dataStatus === DataStatus.OK || dataStatus === DataStatus.LOADING) && (
                    (data['3rd'].inProgress || data['3rd'].completed || data['3rd'].cancelled) ? (
                        <>
                            <ReactApexChart
                                type="pie"
                                // @ts-expect-error: ReactApexChart type definitions may not match the options structure
                                options={getChartConfig('3rd').options}
                                series={getChartConfig('3rd').series}
                                height={420}
                            />
                        </>
                    ) : (
                        <div>No data to display</div>
                    )
                )}
            </div>
        </div>
    );
};

export default ChartInspectionsStatusByMarket;
